import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { V2AdminListPersonaResearchResponses } from '@workspace/api-backend'
import { v2AdminListPersonaResearchOptions } from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { useState } from 'react'

type SearchParams = {
  page?: number
  status?: string
}

export const Route = createFileRoute('/admin/investors/persona-research')({
  component: AdminInvestorPersonaResearchPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as string) || undefined,
  }),
})

type PersonaResearchItem = V2AdminListPersonaResearchResponses[200]['data']['items'][number]

const LIMIT = 50
const PERSONA_RESEARCH_STATUSES = [
  'PENDING',
  'ENQUEUED',
  'ACTIVE',
  'COMPLETED',
  'FAILED',
  'CANCELED',
] as const
type PersonaStatus = (typeof PERSONA_RESEARCH_STATUSES)[number]

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  PENDING: 'secondary',
  ENQUEUED: 'warning',
  ACTIVE: 'warning',
  COMPLETED: 'success',
  FAILED: 'destructive',
  CANCELED: 'secondary',
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function AdminInvestorPersonaResearchPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [selectedStatus, setSelectedStatus] = useState<PersonaStatus | ''>(
    (search.status as PersonaStatus) ?? ''
  )
  const currentPage = search.page ?? 1

  const { data: response, isLoading } = useQuery(
    v2AdminListPersonaResearchOptions({
      query: { page: currentPage, status: selectedStatus || undefined },
    })
  )

  const items: PersonaResearchItem[] = response?.data?.items ?? []
  const total: number = response?.data?.total ?? 0
  const totalPages = Math.ceil(total / LIMIT)

  const columns: TableColumn<PersonaResearchItem>[] = [
    {
      header: 'Run ID',
      width: '120px',
      render: (item) => <span className="font-mono text-xs">{item.id.slice(0, 8)}</span>,
    },
    {
      header: 'Investor',
      render: (item) => (
        <Link
          to="/admin/investors/$id"
          params={{ id: item.investorId }}
          className="font-medium text-primary hover:underline"
        >
          {item.investorName ?? item.investorId.slice(0, 8)}
        </Link>
      ),
    },
    {
      header: 'Status',
      width: '120px',
      render: (item) => (
        <Badge variant={STATUS_VARIANT[item.status] ?? 'secondary'} size="sm">
          {item.status}
        </Badge>
      ),
    },
    {
      header: 'Version',
      width: '180px',
      render: (item) => <CellText className="font-mono text-xs">{item.version}</CellText>,
    },
    {
      header: 'Archetype',
      width: '120px',
      render: (item) => <CellText>{item.archetype ?? '—'}</CellText>,
    },
    {
      header: 'Error',
      render: (item) =>
        item.error ? (
          <span className="block max-w-md truncate text-xs text-destructive" title={item.error}>
            {item.error}
          </span>
        ) : (
          <CellText>—</CellText>
        ),
    },
    {
      header: 'Created',
      width: '160px',
      render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
    },
    {
      header: 'Updated',
      width: '160px',
      render: (item) => <CellText>{formatDate(item.updatedAt)}</CellText>,
    },
  ]

  return (
    <div className="w-full space-y-4">
      <div className="flex gap-2 flex-wrap">
        {(['', ...PERSONA_RESEARCH_STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setSelectedStatus(s)
              void navigate({ search: { page: 1, status: s || undefined } })
            }}
            className={`rounded px-3 py-1 text-sm ${
              selectedStatus === s
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No persona research requests found."
        keyExtractor={(item) => item.id}
      />

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={(p) =>
          void navigate({ search: { page: p, status: selectedStatus || undefined } })
        }
      />
    </div>
  )
}
